import React from "react";
import ServicePage from "./ServicePage";

export default function ShopifyDevelopment() {
  return (
    <ServicePage
      slug="shopify-development"
      eyebrow="Service · 01"
      title="Shopify"
      titleAccent="Development."
      intro="Custom themes, Plus-grade storefronts, headless React fronts and public Shopify apps. Built for speed, conversion and editor sanity — not just for the demo screenshot."
      capabilities={[
        {
          title: "Custom theme & sections",
          desc: "Liquid themes built from a clean foundation. Section-driven, schema-friendly, easy for the marketing team to operate without dev tickets.",
          points: [
            "Section + block architecture mapped to your merchandising flow",
            "App-block ready, no theme-edit lock-in",
            "Editor copy, presets and translations baked in",
          ],
        },
        {
          title: "Headless storefronts",
          desc: "Hydrogen / Next.js fronts on top of Storefront API. Used when you genuinely outgrow Liquid — not because it's trendy.",
          points: [
            "Cart + checkout handoff with no surprises",
            "ISR / streaming for product detail pages",
            "Image pipelines and CDN tuning",
          ],
        },
        {
          title: "Shopify apps (public & private)",
          desc: "Public Shopify apps with billing, OAuth, GDPR webhooks. Internal apps for ops automation, ERP/PIM glue and merchandising.",
          points: [
            "Idempotent webhook processing with retries",
            "Background queues for bulk operations",
            "App Bridge / Polaris admin UIs",
          ],
        },
        {
          title: "Checkout extensibility migration",
          desc: "Off checkout.liquid onto Shopify Plus checkout extensibility — without breaking promos, payments or upsells.",
          points: [
            "Audit + map of every existing customization",
            "Pixel-perfect parity with rollback plan",
            "Phased rollout on a percentage of traffic",
          ],
        },
      ]}
      process={[
        { title: "Audit & scope", desc: "Read the theme, the apps, the GA / Lighthouse data. Land on a tight scope and a real timeline." },
        { title: "Build", desc: "Short, traceable PRs. Staging mirrors prod. You see progress every few days, not at the end." },
        { title: "Launch", desc: "Soft launch behind a password, smoke test, cutover with a documented rollback path." },
        { title: "Aftercare", desc: "Two weeks of post-launch hardening — monitoring, edge cases, the long tail of small fixes." },
      ]}
      outcomes={[
        { kpi: "Performance", title: "Sub-2s LCP", desc: "Real-world Core Web Vitals on mobile, not just lab numbers." },
        { kpi: "Conversion", title: "Cleaner funnel", desc: "Frictionless cart and PDP — fewer drop-offs at the moments that count." },
        { kpi: "Team", title: "Editor-friendly CMS", desc: "Marketing ships campaigns without a dev ticket. Less ops tax over time." },
      ]}
      techHighlights={[
        "Shopify Liquid",
        "Shopify Plus",
        "Hydrogen / Next.js",
        "Storefront API",
        "Admin GraphQL API",
        "Polaris / App Bridge",
        "Webhooks & Functions",
        "Tailwind / vanilla-extract",
      ]}
      faqs={[
        {
          q: "Theme rebuild vs. patch the current one?",
          a: "Depends on debt. If the theme is on a recent Online Store 2.0 foundation and the issues are localized, surgical fixes win. If it's been hacked through 5+ devs and three apps wired into theme.liquid, a clean rebuild is usually faster and cheaper than chasing regressions.",
        },
        {
          q: "Do I need to go headless?",
          a: "Most stores do not. Headless makes sense when you have unique content needs, multi-region complexity, or a separate non-commerce stack you want to share. Otherwise a well-built Liquid theme outperforms a mediocre headless build every time.",
        },
        {
          q: "Can you work alongside our existing team?",
          a: "Yes — embedded into your repo, your standups, your tooling. I prefer that for anything beyond a few weeks of work.",
        },
        {
          q: "How do you handle launches?",
          a: "Password-protected staging with the real apps connected, a written cutover plan, a smoke-test checklist, and a rollback path in writing. No surprises.",
        },
      ]}
    />
  );
}
